import { DBModel } from './DBModel';

export class TAGInvItemType extends DBModel {
  deleted: boolean = false;

  category: string = '';

  name: string = '';
  description: string = '';

  additionalInfo: string = '';

  instances: TAGInvItemInstance[] = [];

  constructor(info: any = {}) {
    super();
    this.deserialize(info);
  }

  findInstance(idOrCode: string) {
    return this.instances.find(
      (instance) => instance.itemid == idOrCode || instance.barcodes.includes(idOrCode),
    );
  }

  override serialize() {
    var obj: any = super.serialize();

    obj.deleted = this.deleted;

    obj.category = this.category;

    obj.name = this.name;
    obj.description = this.description;

    obj.additionalInfo = this.additionalInfo;

    obj.instances = this.instances.map((instance) => instance.serialize());

    return obj;
  }

  override deserialize(data: any) {
    super.deserialize(data);

    if (data.deleted != undefined) this.deleted = data.deleted;

    if (data.category != null) this.category = data.category;

    if (data.name != null) this.name = data.name;
    if (data.description != null) this.description = data.description;

    if (data.additionalInfo != null) this.additionalInfo = data.additionalInfo;

    if (data.instances != null)
      this.instances = data.instances.map((instance) => new TAGInvItemInstance(instance));

    return this;
  }
}

export class TAGInvItemInstance extends DBModel {
  deleted: boolean = false;

  barcodes: string[] = [];

  itemid: string = '';
  createdate: Date = new Date();

  history: TAGInvHistoryEvent[] = undefined;

  constructor(info: any = {}) {
    super();
    this.deserialize(info);
  }

  override serialize() {
    var obj: any = super.serialize();

    obj.deleted = this.deleted;

    obj.barcodes = this.barcodes;

    obj.itemid = this.itemid;
    obj.createdate = this.createdate;

    obj.history = this.history ? this.history.map((event) => event.serialize()) : [];

    return obj;
  }

  override deserialize(data: any) {
    super.deserialize(data);

    if (data.deleted != undefined) this.deleted = data.deleted;

    if (data.barcodes != null) this.barcodes = data.barcodes;

    if (data.itemid != null) this.itemid = data.itemid;
    if (data.createdate != null) this.createdate = new Date(data.createdate);

    if (data.history != null)
      this.history = data.history.map((event) => new TAGInvHistoryEvent(event));

    return this;
  }
}

export class TAGInvTransaction extends DBModel {
  location: string = '';
  user: string = '';
  event: string = '';

  startdate: Date = new Date();
  enddate: Date = null;

  items: { itemid: string; instanceid: string; back: Date }[] = [];

  constructor(info: any = {}) {
    super();
    this.deserialize(info);
  }

  override serialize() {
    var obj: any = super.serialize();

    obj.location = this.location;
    obj.user = this.user;
    obj.event = this.event;

    obj.startdate = this.startdate;
    obj.enddate = this.enddate;

    obj.items = this.items;

    return obj;
  }

  override deserialize(data: any) {
    super.deserialize(data);

    if (data.location != null) this.location = data.location;
    if (data.user != null) this.user = data.user;
    if (data.event != null) this.event = data.event;

    if (data.startdate != null) this.startdate = new Date(data.startdate);
    if (data.enddate != null) this.enddate = new Date(data.enddate);

    if (data.items != null)
      this.items = data.items.map((item) => {
        return {
          itemid: item.itemid,
          instanceid: item.instanceid,
          back: item.back ? new Date(item.back) : null,
        };
      });

    return this;
  }

  containsItemInstance(itemid: string, instanceid: string) {
    return (
      this.items.findIndex((item) => item.itemid == itemid && item.instanceid == instanceid) != -1
    );
  }

  containsItem(itemid: string) {
    return this.items.findIndex((item) => item.itemid == itemid) != -1;
  }

  findItem(itemid: string, instanceid: string) {
    return this.items.find((item) => item.itemid == itemid && item.instanceid == instanceid);
  }

  allItemsBack() {
    return this.items.every((item) => item.back != null);
  }
}

export class TAGInvItemGroup extends DBModel {
  deleted: boolean = false;

  name: string = '';
  items: { itemid: string; instanceid: string }[] = [];

  constructor(info: any = {}) {
    super();
    this.deserialize(info);
  }

  override serialize() {
    var obj: any = super.serialize();

    obj.deleted = this.deleted;

    obj.name = this.name;
    obj.items = this.items;

    return obj;
  }

  override deserialize(data: any) {
    super.deserialize(data);

    if (data.deleted != undefined) this.deleted = data.deleted;

    if (data.name != null) this.name = data.name;

    if (data.items != null)
      this.items = data.items.map((item) => {
        return {
          itemid: item.itemid,
          instanceid: item.instanceid,
          back: item.back ? new Date(item.back) : null,
        };
      });

    return this;
  }

  containsItemInstance(itemid: string, instanceid: string) {
    return (
      this.items.findIndex((item) => item.itemid == itemid && item.instanceid == instanceid) != -1
    );
  }

  containsItem(itemid: string) {
    return this.items.findIndex((item) => item.itemid == itemid) != -1;
  }
}

export enum TAGInvHistoryEventType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  CHECKOUT = 'CHECKOUT',
  CHECKIN = 'CHECKIN',
}

export class TAGInvHistoryEvent extends DBModel {
  eventtype: TAGInvHistoryEventType = undefined;
  date: Date = undefined;
  userid: string = undefined;
  transactionid: string = undefined;
  message: string = undefined;

  constructor(historyEvent: any) {
    super();
    this.deserialize(historyEvent);
  }

  override serialize() {
    var obj: any = super.serialize();

    obj.eventtype = this.eventtype;
    obj.date = this.date;
    obj.userid = this.userid;
    obj.transactionid = this.transactionid;
    obj.message = this.message;

    return obj;
  }

  override deserialize(data: any) {
    super.deserialize(data);

    if (data.eventtype != undefined) this.eventtype = data.eventtype;
    if (data.date != undefined) this.date = new Date(data.date);
    if (data.userid != undefined) this.userid = data.userid;
    if (data.transactionid != undefined) this.transactionid = data.transactionid;
    if (data.message != undefined) this.message = data.message;

    return this;
  }
}
